import { useEffect, useMemo, useState } from 'react'
import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import {
  DEFAULT_PAGE_SIZE,
  type CompanyListFilters,
  type ContactListFilters,
  type Page,
  type ReviewListFilters,
  type AppSettings,
  type ContactDetail,
  type CreditsUsage,
  type ExportRecord,
  type JobContactItem,
  type ReviewItem,
} from './api'
import type {
  AuditEvent,
  AuditListFilters,
  RepairRecord,
  RepairStatus,
  CompanyDetail,
  CompanyListRow,
  ContactRow,
  JobCompanyRow,
  JobSummary,
  MeResponse,
  OutreachFeedback,
  ProductStats,
  SavedView,
  ZohoImportRun,
  ZohoModule,
  ZohoSpamRecord,
  ZohoStatusResponse,
  ZohoViewsResponse,
  UsersResponse,
} from '@profiler/shared'
import { useApi } from './auth'

/** Poll cadence while a job is moving. Slow enough not to hammer the API. */
export const LIVE_REFRESH_MS = 10_000

/**
 * PAUSED counts as live: a job pauses when it hits a credit cap or the kill
 * switch, and an admin lifting either resumes it without the screen being
 * reloaded. A screen that stopped polling would show a frozen job that is in
 * fact running again.
 */
const LIVE_STATUSES: ReadonlySet<JobSummary['status']> = new Set<JobSummary['status']>(['RUNNING', 'PAUSED'])

export function isLive(job: JobSummary | undefined): boolean {
  return job ? LIVE_STATUSES.has(job.status) : false
}

export const qk = {
  me: ['me'] as const,
  settings: ['settings'] as const,
  creditsUsage: ['credits-usage'] as const,
  dashboard: ['dashboard'] as const,
  savedViews: ['saved-views'] as const,
  jobs: ['jobs'] as const,
  job: (jobId: string) => ['jobs', jobId] as const,
  companies: (jobId: string) => ['jobs', jobId, 'companies'] as const,
  jobContacts: (jobId: string) => ['jobs', jobId, 'contacts'] as const,
  reviews: (jobId: string, status: ReviewItem['status']) => ['jobs', jobId, 'reviews', status] as const,
  contacts: ['contacts'] as const,
  /** Every company across all jobs, as the Companies screen lists them. */
  allCompanies: ['companies'] as const,
  allReviews: ['reviews'] as const,
  audit: ['audit'] as const,
  /** Every repair list and record, so one decision can refresh them all. */
  repairs: ['repairs'] as const,
  repairQueue: (status: RepairStatus) => ['repairs', 'queue', status] as const,
  repair: (domain: string) => ['repairs', 'one', domain] as const,
  /** What the mail did, and what that says about the gates. */
  outreachFeedback: ['outreach', 'feedback'] as const,
  company: (domain: string) => ['companies', domain] as const,
  contact: (linkedinUrl: string) => ['contact', linkedinUrl] as const,
  exportJob: (exportId: string) => ['exports', exportId] as const,
  /** The Zoho CRM block as Settings renders it, and the views the wizard offers. */
  zohoStatus: ['zoho', 'status'] as const,
  zohoViews: (module: ZohoModule) => ['zoho', 'views', module] as const,
  zohoImport: (runId: string | null) => ['zoho', 'import', runId ?? 'latest'] as const,
  zohoSpam: ['zoho', 'spam'] as const,
  /** Everybody who has signed in, as the Users screen lists them. */
  users: ['users'] as const,
}

export function useCompanyDetail(domain: string | null): UseQueryResult<CompanyDetail> {
  const api = useApi()
  return useQuery({
    queryKey: qk.company(domain ?? ''),
    queryFn: () => api.getCompany(domain as string),
    enabled: domain !== null,
  })
}

export function useAllReviews(
  filters: ReviewListFilters,
  cursor: string | null,
  /** The page size on screen. The request asks for exactly this many rows. */
  limit: number = DEFAULT_PAGE_SIZE,
): UseQueryResult<Page<ReviewItem>> {
  const api = useApi()
  return useQuery({
    // The size is part of the identity of a page: the same cursor at a
    // different size is a different set of rows, and reusing the cached one
    // would show twenty-five rows in a grid asking for a hundred.
    queryKey: [...qk.allReviews, filters, cursor, limit],
    queryFn: () => api.listAllReviews(filters, cursor, limit),
    placeholderData: (previous) => previous,
  })
}

/**
 * One page of the audit trail.
 *
 * The filters are part of the key because the server applies them: two
 * different filters are two different results, and caching them under one key
 * would show the wrong page. No polling — history does not change behind you,
 * and a log that reorders itself while you read it is a log nobody trusts.
 */
export function useAudit(
  filters: AuditListFilters,
  cursor: string | null,
  limit: number = DEFAULT_PAGE_SIZE,
  enabled = true,
): UseQueryResult<Page<AuditEvent>> {
  const api = useApi()
  return useQuery({
    queryKey: [...qk.audit, filters, cursor, limit],
    queryFn: () => api.listAudit(filters, cursor, limit),
    enabled,
    placeholderData: (previous) => previous,
  })
}

export function useRepairs(
  status: RepairStatus,
  cursor: string | null,
  limit: number = DEFAULT_PAGE_SIZE,
): UseQueryResult<Page<RepairRecord>> {
  const api = useApi()
  return useQuery({
    queryKey: [...qk.repairQueue(status), cursor, limit],
    queryFn: () => api.listRepairs(status, cursor, limit),
    placeholderData: (previous) => previous,
  })
}

/** One company's repair. A 404 is the common case — never examined — and is not retried. */
export function useRepair(domain: string | null): UseQueryResult<RepairRecord> {
  const api = useApi()
  return useQuery({
    queryKey: qk.repair(domain ?? ''),
    queryFn: () => api.getRepair(domain ?? ''),
    enabled: domain !== null,
    retry: false,
  })
}

export function useAllCompanies(
  filters: CompanyListFilters,
  cursor: string | null,
  /** The page size on screen. The request asks for exactly this many rows. */
  limit: number = DEFAULT_PAGE_SIZE,
): UseQueryResult<Page<CompanyListRow>> {
  const api = useApi()
  return useQuery({
    // The size is part of the identity of a page: the same cursor at a
    // different size is a different set of rows, and reusing the cached one
    // would show twenty-five rows in a grid asking for a hundred.
    queryKey: [...qk.allCompanies, filters, cursor, limit],
    queryFn: () => api.listAllCompanies(filters, cursor, limit),
    placeholderData: (previous) => previous,
  })
}

export function useMe(): UseQueryResult<MeResponse> {
  const api = useApi()
  return useQuery({ queryKey: qk.me, queryFn: () => api.me(), staleTime: 5 * 60_000 })
}

/** The Users screen's list. Gated on the caller being an administrator, which the server enforces anyway. */
export function useUsers(enabled: boolean): UseQueryResult<UsersResponse> {
  const api = useApi()
  return useQuery({ queryKey: qk.users, queryFn: () => api.listUsers(), enabled })
}

export function useOutreachFeedback(): UseQueryResult<OutreachFeedback> {
  const api = useApi()
  return useQuery({ queryKey: qk.outreachFeedback, queryFn: () => api.outreachFeedback() })
}

/**
 * The dashboard snapshot.
 *
 * Refetched on an interval because the figures are rebuilt by a sweep every few
 * minutes and this is a screen people leave open — but slowly, because the
 * numbers themselves only move that fast. Polling faster would ask the server
 * for the same snapshot it already sent.
 */
export function useDashboard(): UseQueryResult<ProductStats> {
  const api = useApi()
  return useQuery({
    queryKey: qk.dashboard,
    queryFn: () => api.dashboard(),
    staleTime: 60_000,
    refetchInterval: 2 * 60_000,
  })
}

/**
 * The signed-in person's saved views, and the one mutation that changes them.
 *
 * One list, replaced whole: saving, renaming, reordering and deleting are all
 * the same operation on something this small, and a whole-list write cannot
 * leave a half-applied change behind.
 */
export function useSavedViews(): UseQueryResult<SavedView[]> {
  const api = useApi()
  return useQuery({
    queryKey: qk.savedViews,
    queryFn: async () => (await api.savedViews()).views,
    staleTime: 5 * 60_000,
  })
}

export function useJobs(): UseQueryResult<JobSummary[]> {
  const api = useApi()
  return useQuery({
    queryKey: qk.jobs,
    queryFn: () => api.listJobs(),
    // Driven by the data, not by a flag: the list keeps polling exactly as long
    // as something on it is still moving, then stops on its own.
    refetchInterval: (query) => (query.state.data?.some(isLive) ? LIVE_REFRESH_MS : false),
  })
}

export function useJob(jobId: string): UseQueryResult<JobSummary> {
  const api = useApi()
  return useQuery({
    queryKey: qk.job(jobId),
    queryFn: () => api.getJob(jobId),
    refetchInterval: (query) => (isLive(query.state.data) ? LIVE_REFRESH_MS : false),
    enabled: jobId !== '',
  })
}

export function useCompanies(
  jobId: string,
  live: boolean,
  cursor: string | null = null,
  limit: number = DEFAULT_PAGE_SIZE,
  status?: string,
): UseQueryResult<Page<JobCompanyRow>> {
  const api = useApi()
  return useQuery({
    queryKey: [...qk.companies(jobId), cursor, limit, status ?? ''],
    queryFn: () => api.listCompanies(jobId, cursor, limit, status),
    refetchInterval: live ? LIVE_REFRESH_MS : false,
    enabled: jobId !== '',
    placeholderData: (previous) => previous,
  })
}

export function useJobContacts(
  jobId: string,
  live: boolean,
  cursor: string | null = null,
  limit: number = DEFAULT_PAGE_SIZE,
  gate?: string,
): UseQueryResult<Page<JobContactItem>> {
  const api = useApi()
  return useQuery({
    queryKey: [...qk.jobContacts(jobId), cursor, limit, gate ?? ''],
    queryFn: () => api.listJobContacts(jobId, cursor, limit, gate),
    refetchInterval: live ? LIVE_REFRESH_MS : false,
    enabled: jobId !== '',
    placeholderData: (previous) => previous,
  })
}

/**
 * Every contact in one job, whole.
 *
 * Separate from {@link useJobContacts}, which serves a single page for the job
 * detail tab. This one backs a screen that filters in the browser, and a browser
 * filter over one server page is a filter that hides rows and still offers a
 * next page.
 */
export function useAllJobContacts(jobId: string): UseQueryResult<{
  items: JobContactItem[]
  truncated: boolean
}> {
  const api = useApi()
  return useQuery({
    queryKey: [...qk.jobContacts(jobId), 'all'],
    queryFn: () => api.collectJobContacts(jobId),
    enabled: jobId !== '',
    placeholderData: (previous) => previous,
  })
}

export function useReviews(
  jobId: string,
  status: ReviewItem['status'],
  live: boolean,
  cursor: string | null = null,
  limit: number = DEFAULT_PAGE_SIZE,
): UseQueryResult<Page<ReviewItem>> {
  const api = useApi()
  return useQuery({
    queryKey: [...qk.reviews(jobId, status), cursor, limit],
    queryFn: () => api.listReviews(jobId, status, cursor, limit),
    refetchInterval: live ? LIVE_REFRESH_MS : false,
    enabled: jobId !== '',
    placeholderData: (previous) => previous,
  })
}

/**
 * One page of contacts, keyed by the filters that produced it.
 *
 * The filters are part of the key because they are part of the QUERY now — the
 * server applies them, so two different filters are two different results and
 * caching them under one key would show the wrong page.
 *
 * `placeholderData` keeps the previous page on screen while the next loads, so
 * paging does not flash an empty grid.
 */
export function useContacts(
  filters: ContactListFilters,
  cursor: string | null,
  /** The page size on screen. The request asks for exactly this many rows. */
  limit: number = DEFAULT_PAGE_SIZE,
  enabled = true,
): UseQueryResult<Page<ContactRow>> {
  const api = useApi()
  return useQuery({
    // The size is part of the identity of a page: the same cursor at a
    // different size is a different set of rows, and reusing the cached one
    // would show twenty-five rows in a grid asking for a hundred.
    queryKey: [...qk.contacts, filters, cursor, limit],
    queryFn: () => api.listContacts(filters, cursor, limit),
    enabled,
    placeholderData: (previous) => previous,
  })
}

export function useContactDetail(linkedinUrl: string | null): UseQueryResult<ContactDetail> {
  const api = useApi()
  return useQuery({
    queryKey: qk.contact(linkedinUrl ?? ''),
    queryFn: () => api.getContact(linkedinUrl ?? ''),
    enabled: linkedinUrl !== null,
  })
}

export function useSettings(enabled: boolean): UseQueryResult<AppSettings> {
  const api = useApi()
  return useQuery({ queryKey: qk.settings, queryFn: () => api.settings(), enabled })
}

export function useCreditsUsage(enabled: boolean): UseQueryResult<CreditsUsage> {
  const api = useApi()
  return useQuery({ queryKey: qk.creditsUsage, queryFn: () => api.creditsUsage(), enabled })
}

/**
 * The Zoho block. Polled while something is in flight — a connect just asked
 * for, an import running — because the worker stamps the outcome into the
 * block and nothing else tells the screen.
 */
export function useZohoStatus(enabled: boolean, live: boolean): UseQueryResult<ZohoStatusResponse> {
  const api = useApi()
  return useQuery({
    queryKey: qk.zohoStatus,
    queryFn: () => api.zohoStatus(),
    enabled,
    refetchInterval: live ? 3_000 : false,
  })
}

export function useZohoImportRun(
  runId: string | null,
  enabled: boolean,
  live: boolean,
): UseQueryResult<{ run: ZohoImportRun | null }> {
  const api = useApi()
  return useQuery({
    queryKey: qk.zohoImport(runId),
    queryFn: () => api.zohoImportRun(runId ?? undefined),
    enabled,
    refetchInterval: live ? 3_000 : false,
  })
}

export function useZohoSpam(enabled: boolean): UseQueryResult<{ items: ZohoSpamRecord[] }> {
  const api = useApi()
  return useQuery({ queryKey: qk.zohoSpam, queryFn: () => api.zohoSpam(), enabled, staleTime: 60_000 })
}

export function useZohoViews(
  enabled: boolean,
  module: ZohoModule = 'Accounts',
): UseQueryResult<ZohoViewsResponse> {
  const api = useApi()
  return useQuery({
    queryKey: qk.zohoViews(module),
    queryFn: () => api.zohoViews(module),
    enabled,
    staleTime: 60_000,
  })
}

/**
 * How long an export is watched before the screen stops asking.
 *
 * The export Lambda is capped at 300 seconds (EXPORT_WORKER_TIMEOUT in
 * infra/lib/app-stack.ts), so a record still reading pending or running six
 * minutes on is a worker that died without ever writing `failed` — and nothing
 * left running will move it. Without the bound that record was polled every two
 * seconds for ever, in a background tab nobody was looking at.
 */
export const EXPORT_POLL_LIMIT_MS = 6 * 60_000

/**
 * Whether an export has stopped answering.
 *
 * The poll now stops at a deadline, which fixed a background tab asking every
 * two seconds for ever — and created a quieter problem in its place: the record
 * still reads `pending`, so the screen goes on saying "Building" and nothing
 * ever arrives. Silence is the one thing a progress indicator must not mean.
 *
 * Exported so the two screens that show an export agree about it. Written as a
 * function of the record rather than as a flag on the query, because both of
 * them already have the record and neither should re-derive the rule.
 */
/**
 * Re-render once the export deadline passes.
 *
 * The poll stops on its own, but stopping is not an event anything observes —
 * so without this the screen keeps whatever it last rendered, which is
 * "Building", for ever. One timer, armed from the record's own age, that ticks
 * exactly once and lets `exportStalled` be read again.
 */
export function useExportDeadline(record: { status?: string; requestedAt?: string } | undefined): boolean {
  const [, setTick] = useState(0)
  const requestedAt = record?.requestedAt
  const live = record?.status === 'pending' || record?.status === 'running'

  useEffect(() => {
    if (!live || !requestedAt) return
    const started = Date.parse(requestedAt)
    if (!Number.isFinite(started)) return
    const remaining = started + EXPORT_POLL_LIMIT_MS - Date.now()
    if (remaining <= 0) return
    const timer = setTimeout(() => setTick((n) => n + 1), remaining + 250)
    return () => clearTimeout(timer)
  }, [live, requestedAt])

  return exportStalled(record)
}

export function exportStalled(
  record: { status?: string; requestedAt?: string } | undefined,
  now: number = Date.now(),
): boolean {
  if (record?.status !== 'pending' && record?.status !== 'running') return false
  const started = record.requestedAt ? Date.parse(record.requestedAt) : Number.NaN
  if (!Number.isFinite(started)) return false
  return now - started > EXPORT_POLL_LIMIT_MS
}

/**
 * Poll one export until it settles. The workbook is built on a queue because a
 * thousand-company sheet outlives API Gateway's 30 second integration timeout,
 * so there is nothing to show until the worker says `ready`.
 */
export function useExportJob(exportId: string | null): UseQueryResult<ExportRecord> {
  const api = useApi()
  // Timed from this screen's own clock rather than from the record's
  // requestedAt: a browser running a few minutes fast would read a freshly
  // queued export as one that had already outlived its worker.
  const watchingSince = useMemo(() => Date.now(), [exportId])

  return useQuery({
    queryKey: qk.exportJob(exportId ?? ''),
    queryFn: () => api.getExport(exportId ?? ''),
    enabled: exportId !== null,
    refetchInterval: (query) => {
      const status = query.state.data?.status
      if (status !== 'pending' && status !== 'running') return false
      return Date.now() - watchingSince < EXPORT_POLL_LIMIT_MS ? 2_000 : false
    },
    /*
     * The deadline has to be re-evaluated, not merely passed.
     *
     * `refetchInterval` is only consulted after a fetch, so once polling stops
     * nothing re-renders and the screen keeps whatever it last said — which was
     * "Building", for ever. `refetchOnMount` and a `structuralSharing` change do
     * not help: the record itself never moves. The screens therefore derive
     * their own verdict from the record's age via `exportStalled`, and this
     * hook's job is only to stop asking.
     */
    // Keep polling when the tab is not focused. Building a large workbook takes
    // long enough that people switch away, and without this the poll pauses and
    // the finished download never starts until they come back and click again.
    refetchIntervalInBackground: true,
  })
}
